import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from './CartContext';
import { auth, db } from '../../Firebaseconfig';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, addDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import Navbar from '../MainComponents/Navbar';
import Footer from '../MainComponents/Footer';
import './CheckoutPage.css';
import { FaMapMarkerAlt, FaCreditCard, FaMoneyBillWave, FaTrash, FaPlus, FaMinus, FaShoppingBag } from 'react-icons/fa';

export default function CheckoutPage() {
  const { cartItems, cartCompany, cartTotal, removeFromCart, updateQuantity, clearCart } = useCart();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState('Cash'); // Default
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  const [selectedPromoCode, setSelectedPromoCode] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);

  // Filter eligible vouchers (unused, global or matching restaurant id/uid)
  const eligiblePromoCodes = (userData?.promoCodes || []).filter(c => {
    if (c.status !== 'unused') return false;
    if (c.scope === 'global') return true;
    if (c.scope === 'restaurant' && c.restaurantId === (cartCompany?.id || cartCompany?.uid)) return true;
    return false;
  });

  // Calculate discount value
  useEffect(() => {
    if (!selectedPromoCode) {
      setDiscountAmount(0);
      return;
    }
    if (selectedPromoCode.type === 'percentage') {
      const discount = cartTotal * (selectedPromoCode.value / 100);
      setDiscountAmount(discount);
    } else if (selectedPromoCode.type === 'value') {
      const discount = Math.min(selectedPromoCode.value, cartTotal);
      setDiscountAmount(discount);
    }
  }, [selectedPromoCode, cartTotal]);

  const finalTotal = Math.max(0, cartTotal - discountAmount + 2.99);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        try {
          const docSnap = await getDoc(doc(db, 'users', user.uid));
          if (docSnap.exists()) {
            const data = docSnap.data();
            setUserData(data);
            if (data.addresses && data.addresses.length > 0) {
              setSelectedAddress(data.addresses[0]);
            }
          }
        } catch (err) {
          console.error("Error fetching user data:", err);
        }
      } else {
        navigate('/login');
      }
      setLoadingUser(false);
    });
    return () => unsub();
  }, [navigate]);

  const handlePlaceOrder = async () => {
    if (!currentUser || !cartCompany || cartItems.length === 0) return;
    
    if (!selectedAddress) {
      alert("Please add or select a delivery address.");
      return;
    }

    setIsPlacingOrder(true);

    try {
      const addressString = [selectedAddress.street, selectedAddress.city].filter(Boolean).join(', ');

      // Format itemsText for backward compatibility with existing dashboards
      const itemsText = cartItems.map(item => 
        `${item.quantity}x ${item.name}` + 
        (item.customizations ? ` [${item.customizations.join(', ')}]` : '')
      ).join(' | ');

      const orderData = {
        customerId:       currentUser.uid,
        customerEmail:    currentUser.email || '',
        customerName:     currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown Customer',
        address:          addressString || 'No location',
        
        // Use first product ID/Name for backward compatibility
        productId:        cartItems[0]?.productId || 'multi',
        productName:      cartItems.length === 1 ? cartItems[0].name : `${cartItems.length} items`,
        itemsText:        itemsText,
        
        // Full array for the new schema
        items:            cartItems,
        
        category:         cartItems[0]?.category || 'General',
        tags:             cartItems.map(i => i.category?.toUpperCase()).filter(Boolean),
        total:            parseFloat(finalTotal.toFixed(2)),
        subtotal:         parseFloat(cartTotal.toFixed(2)),
        discountAmount:   parseFloat(discountAmount.toFixed(2)),
        appliedPromoCode: selectedPromoCode ? selectedPromoCode.code : null,
        
        companyId:        cartCompany.id || cartCompany.uid,
        companyName:      cartCompany.businessName || cartCompany.name || 'Restaurant',
        companyEmail:     cartCompany.businessEmail || cartCompany.email || '',
        
        paymentMethod:    selectedPayment,
        status:           'pending',
        createdAt:        serverTimestamp(),
      };

      await addDoc(collection(db, 'orders'), orderData);

      if (selectedPromoCode) {
        // Update user's promo code status to 'used'
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
          const uData = userSnap.data();
          const updatedPromoCodes = (uData.promoCodes || []).map(c => {
            if (c.code === selectedPromoCode.code && c.status === 'unused') {
              return { ...c, status: 'used', usedAt: new Date().toISOString() };
            }
            return c;
          });
          await setDoc(userDocRef, { promoCodes: updatedPromoCodes }, { merge: true });
        }
      }
      
      clearCart();
      navigate('/dashboard', { state: { orderSuccess: true } });
      
    } catch (err) {
      console.error('Order failed:', err);
      alert(`Order failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  if (loadingUser) {
    return (
      <div className="checkout-page">
        <Navbar />
        <div style={{ display: 'flex', justifyContent: 'center', padding: '100px 0', color: '#fff' }}>Loading...</div>
        <Footer />
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="checkout-page">
        <Navbar />
        <div className="checkout-empty">
          <FaShoppingBag className="checkout-empty-icon" />
          <h2 className="checkout-empty-title">Your cart is empty</h2>
          <p className="checkout-empty-desc">Looks like you haven't added anything to your cart yet.</p>
          <Link to="/products" className="place-order-btn" style={{ width: 'auto', textDecoration: 'none' }}>
            Browse Restaurants
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <Navbar />
      
      <div className="checkout-container">
        
        {/* ── LEFT: Checkout Details ── */}
        <div className="checkout-left">
          
          <div className="checkout-section">
            <h2 className="checkout-section-title">
              <FaMapMarkerAlt className="checkout-icon" /> Delivery Address
            </h2>
            
            <div className="address-options">
              {userData?.addresses?.map((addr, idx) => (
                <div 
                  key={idx} 
                  className={`address-option ${selectedAddress?.id === addr.id ? 'selected' : ''}`}
                  onClick={() => setSelectedAddress(addr)}
                >
                  <input 
                    type="radio" 
                    className="address-radio"
                    name="address"
                    checked={selectedAddress?.id === addr.id}
                    readOnly
                  />
                  <div className="address-details">
                    <span className="address-label">{addr.label || 'Address'}</span>
                    <span className="address-text">{addr.street}</span>
                    <span className="address-text">
                      {[addr.postalCode, addr.city, addr.country].filter(Boolean).join(', ')}
                    </span>
                  </div>
                </div>
              ))}

              {(!userData?.addresses || userData.addresses.length === 0) && (
                <div className="no-address-msg">You don't have any saved addresses.</div>
              )}

              <Link to="/dashboard" className="add-address-btn">
                + Add or edit addresses in Profile
              </Link>
            </div>
          </div>

          <div className="checkout-section">
            <h2 className="checkout-section-title">
              <FaCreditCard className="checkout-icon" /> Payment Method
            </h2>
            
            <div className="address-options">
              <div 
                className={`address-option ${selectedPayment === 'Cash' ? 'selected' : ''}`}
                onClick={() => setSelectedPayment('Cash')}
              >
                <input 
                  type="radio" 
                  className="address-radio"
                  name="payment"
                  checked={selectedPayment === 'Cash'}
                  readOnly
                />
                <div className="address-details" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                  <FaMoneyBillWave style={{ color: '#4ade80' }} />
                  <span className="address-label">Cash on Delivery</span>
                </div>
              </div>

              {userData?.cards?.map((card, idx) => (
                <div 
                  key={idx} 
                  className={`address-option ${selectedPayment === `Card ending in ${card.last4}` ? 'selected' : ''}`}
                  onClick={() => setSelectedPayment(`Card ending in ${card.last4}`)}
                >
                  <input 
                    type="radio" 
                    className="address-radio"
                    name="payment"
                    checked={selectedPayment === `Card ending in ${card.last4}`}
                    readOnly
                  />
                  <div className="address-details" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                    <FaCreditCard style={{ color: '#94a3b8' }} />
                    <span className="address-label">•••• {card.last4}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* ── RIGHT: Order Summary ── */}
        <div className="checkout-right">
          <div className="order-summary-card">
            <h2 className="checkout-section-title">Order Summary</h2>

            {cartCompany && (
              <div className="summary-company">
                {cartCompany.photoURL || cartCompany.images?.[0] ? (
                  <img src={cartCompany.photoURL || cartCompany.images?.[0]} alt="" className="summary-company-img" />
                ) : (
                  <div className="summary-company-img" style={{ background: '#2d3748', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    🍔
                  </div>
                )}
                <div className="summary-company-info">
                  <span className="summary-company-name">{cartCompany.businessName || cartCompany.name || 'Restaurant'}</span>
                  <span className="summary-company-sub">{cartCompany.businessLocation || 'Partner'}</span>
                </div>
              </div>
            )}

            <div className="summary-items">
              {cartItems.map((item, idx) => (
                <div key={idx} className="summary-item">
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="summary-item-img" />
                  ) : (
                    <div className="summary-item-img" />
                  )}
                  <div className="summary-item-details">
                    <h4 className="summary-item-name">{item.name}</h4>
                    <span className="summary-item-price">${parseFloat(item.price).toFixed(2)}</span>
                    {item.customizations && item.customizations.length > 0 && (
                      <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{item.customizations.join(', ')}</span>
                    )}
                  </div>
                  
                  <div className="summary-item-controls">
                    <button className="qty-btn" onClick={() => updateQuantity(idx, item.quantity - 1)} disabled={item.quantity <= 1}>
                      <FaMinus size={10} />
                    </button>
                    <span className="qty-val">{item.quantity}</span>
                    <button className="qty-btn" onClick={() => updateQuantity(idx, item.quantity + 1)}>
                      <FaPlus size={10} />
                    </button>
                  </div>
                  
                  <button className="remove-btn" onClick={() => removeFromCart(idx)}>
                    <FaTrash size={14} />
                  </button>
                </div>
              ))}
            </div>
            {/* Promo Code Selector */}
            <div className="summary-promo-selector">
              <label htmlFor="promoSelector" className="promo-label">Apply Promo Code</label>
              {eligiblePromoCodes.length === 0 ? (
                <div className="no-eligible-promos">No vouchers available for this order.</div>
              ) : (
                <select
                  id="promoSelector"
                  className="promo-select-dropdown"
                  value={selectedPromoCode ? selectedPromoCode.code : ''}
                  onChange={e => {
                    const found = eligiblePromoCodes.find(c => c.code === e.target.value);
                    setSelectedPromoCode(found || null);
                  }}
                >
                  <option value="">-- Select a Voucher --</option>
                  {eligiblePromoCodes.map((c, idx) => (
                    <option key={idx} value={c.code}>
                      {c.code} ({c.type === 'percentage' ? `${c.value}% OFF` : `$${c.value} OFF`})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="summary-totals">
              <div className="summary-row">
                <span>Subtotal</span>
                <span>${cartTotal.toFixed(2)}</span>
              </div>
              
              {selectedPromoCode && (
                <div className="summary-row promo">
                  <span>Promo Discount ({selectedPromoCode.code})</span>
                  <span className="promo-discount">-${discountAmount.toFixed(2)}</span>
                </div>
              )}
              
              <div className="summary-row">
                <span>Delivery Fee</span>
                <span>$2.99</span>
              </div>
              <div className="summary-row total">
                <span>Total</span>
                <span className="total-price">${finalTotal.toFixed(2)}</span>
              </div>
            </div>

            <button 
              className="place-order-btn" 
              onClick={handlePlaceOrder}
              disabled={isPlacingOrder || !selectedAddress}
            >
              {isPlacingOrder ? 'Processing...' : 'Place Order'}
            </button>
          </div>
        </div>

      </div>
      
      <Footer />
    </div>
  );
}
